import { parseApiError, ApiErrorCode } from '@ooh/contracts';
import type { QueryClient } from '@tanstack/react-query';
import { toast } from '@ooh/ui/components/sonner';
import { reservationQueryKeys } from './queries';
import { apiErrorToUserMessage } from '../../lib/api-error-messages';

// ─── Helper: 409 error code routing ─────────────────────────
// Returns inline error message string for known 409 codes,
// or null if the error should be shown as a toast.

const INLINE_ERROR_CODES = new Set<string>([
  ApiErrorCode.RESERVATION_CONFLICT,
  ApiErrorCode.DISPLACEMENT_REQUIRED,
  ApiErrorCode.STALE_STATE,
]);

export function getInlineErrorCode(error: unknown): string | null {
  const parsed = parseApiError(error);
  if (parsed.statusCode === 409 && parsed.errorCode && INLINE_ERROR_CODES.has(parsed.errorCode)) {
    return parsed.errorCode;
  }
  return null;
}

export function handleMutationError(
  error: unknown,
  onInlineError?: (code: string) => void,
  t?: (key: string) => string,
): void {
  const inlineCode = getInlineErrorCode(error);
  if (inlineCode !== null && onInlineError) {
    onInlineError(inlineCode);
    return;
  }
  const parsed = parseApiError(error);
  if (parsed.errorCode === ApiErrorCode.AVAILABILITY_WINDOW_BLOCKED && t) {
    toast.error(t('toasts.availabilityWindowBlocked'));
    return;
  }
  toast.error(apiErrorToUserMessage(parsed, 'mutation'));
}

// ─── Shared onSuccess invalidation helper ────────────────────

/**
 * Invalidates reservation query caches after a successful mutation.
 * Shared across all mutation hooks to avoid copy-paste drift.
 */
export function invalidateReservationCaches(
  queryClient: QueryClient,
  unitId: number | undefined,
): void {
  void queryClient.invalidateQueries({ queryKey: reservationQueryKeys.lists() });
  if (unitId !== undefined) {
    void queryClient.invalidateQueries({ queryKey: reservationQueryKeys.byUnit(unitId) });
    void queryClient.invalidateQueries({ queryKey: reservationQueryKeys.auditByUnit(unitId) });
  }
}

/**
 * Invalidates reservation and queue caches after a queue mutation.
 *
 * CU-86cb3x6y6: also invalidates reservationQueryKeys.auditByUnit — join/leave/
 * admin-promote all write audit rows (reservation.queue_joined,
 * reservation.queue_left, reservation.queue_removed,
 * reservation.force_release, reservation.queue_promoted) but this helper
 * previously left the "Pełna historia zdarzeń" panel on /media-units/:id
 * stale until a manual page reload.
 *
 * CU-86cb7ctfw: also invalidates reservationQueryKeys.queueByReservationsAll() unconditionally
 * (not gated on mediaUnitId) — the global /reservations list's batched queue
 * query is keyed by the current page's reservation ids, not a media unit id,
 * so every queue mutation (from either surface) must bust it.
 */
export function invalidateQueueCaches(
  queryClient: QueryClient,
  mediaUnitId: number | undefined,
): void {
  void queryClient.invalidateQueries({ queryKey: reservationQueryKeys.lists() });
  void queryClient.invalidateQueries({ queryKey: reservationQueryKeys.queueByReservationsAll() });
  if (mediaUnitId !== undefined) {
    void queryClient.invalidateQueries({ queryKey: reservationQueryKeys.byUnit(mediaUnitId) });
    void queryClient.invalidateQueries({ queryKey: reservationQueryKeys.auditByUnit(mediaUnitId) });
    void queryClient.invalidateQueries({ queryKey: reservationQueryKeys.queueByUnit(mediaUnitId) });
  }
}
